// PowerComposer — slash-command and @-mention trigger detection, pure.
//
// Research finding (.claude/analysis/chat-composer-research.md §3): slash and
// @-mention are a DIFFERENT axis from markdown, and must stay visually and
// behaviourally distinct — "slash commands invoke actions or templates
// while @-mentions reference context... distinct trigger characters".
// bilda already has this half-shaped (sketches/chat/MentionComposerSketch.tsx
// + the CommandPalette/SlashMenu combobox convention); this module is the
// pure decision layer PowerComposer.tsx drives its two comboboxes from.
//
// Each trigger is detected independently from the draft text (+ caret for
// the mention, which is caret-anchored so "...ask @sou" mid-sentence still
// resolves); a "dismissed" flag per trigger tracks an explicit Escape
// without needing component state beyond one remembered query string —
// typing further (a DIFFERENT query) always re-opens the menu, mirroring
// the Editor SlashMenu/CommandPalette convention that a literal trigger
// character is always reachable by backing out.

import * as Core from "@bilda/core";
import { Result } from "@bilda/utils";

// The slash-command trigger fires only when the WHOLE draft starts with
// "/" — a composer-wide command mode, not an inline insert (distinct from
// the @ mention below, which can appear anywhere in the draft).
export type SlashTrigger = { readonly query: string };

export const detectSlashTrigger = (value: string): SlashTrigger | null =>
    value.startsWith("/") ? { query: value.slice(1) } : null;

// The @ mention trigger fires on an unclosed "@query" run ending exactly at
// the caret, preceded by start-of-string or whitespace (so "user@example"
// never triggers — an email-shaped run is not a mention).
export type MentionTrigger = {
    readonly query: string;
    // Index into `value` where the "@" itself sits — the mention picker
    // needs this to know what span to delete once a pick resolves.
    readonly at: number;
};

const MENTION_TAIL = /(?:^|\s)@([\w-]*)$/;

export const detectMentionTrigger = (
    value: string,
    caret: number,
): MentionTrigger | null => {
    const upToCaret = value.slice(0, caret);
    const match = MENTION_TAIL.exec(upToCaret);
    if (match === null || match.index === undefined) return null;
    const leadingWhitespace =
        match[0].startsWith(" ") || match[0].startsWith("\t") ? 1 : 0;
    return { query: match[1] ?? "", at: match.index + leadingWhitespace };
};

// D8: a pick replaces the typed "@query" IN PLACE with the mention token
// (`[title](/library/{id})`, the same link the agent writes), so the
// sentence keeps its meaning. One space follows unless whitespace already
// does; the caret lands after it. An id that does not parse as a resource id
// inserts the bare title rather than a broken link.
export type MentionInsertion = {
    readonly value: string;
    readonly caret: number;
};

export const insertMention = (
    value: string,
    trigger: MentionTrigger,
    caret: number,
    candidate: { readonly id: string; readonly title: string },
): MentionInsertion => {
    const token = Result.match({
        ok: (id: Core.Resource.Id.Id) =>
            Core.ProseLink.mkMention(candidate.title, id),
        err: () => candidate.title,
    })(Core.Resource.Id.to(candidate.id));
    const after = value.slice(caret);
    const before =
        value.slice(0, trigger.at) + (/^\s/.test(after) ? token : `${token} `);
    return { value: before + after, caret: before.length };
};

// A trigger stays open unless the author explicitly dismissed (Escape) the
// SAME query it is currently showing. Typing one more character changes the
// query, which is a different trigger state, so the menu reopens — the
// author is never permanently locked out of the affordance for a whole
// composing session, only for the exact keystroke they backed out of.
export const isDismissed = (
    query: string,
    dismissedQuery: string | null,
): boolean => dismissedQuery !== null && dismissedQuery === query;
